import React, { useState, useEffect, useContext } from 'react';
import { JobAppContext } from '../../GlobalState';

import { getAllForms, listCustomers } from '../../api'

import dayjs from '../../components/libs/dayjs'
import formatDate from '../../utils/formatDate'
import Datetime from 'react-datetime'
import FormDate from './FormDate'

import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

import { Form, Input } from '../../components'
import { Checkbox } from '../partials'
import { ListDropDown } from '../partials/ListDropDown';
import DataList from '../../components/Form/DataList'

import { ContactField } from '../partials/ContactField';
import { LocationField } from '../partials/LocationField';
import CurrencyField from '../Form/CurrencyField';

import { useLocations, useContacts } from '../../hooks';

const AddJobButton = (props) => {

	const { user, apiKey } = useContext(JobAppContext);
	
	// Use react-query hooks for locations and contacts (data is prefetched by NcsDataPrefetcher)
	const { data: locations = [], isLoading: isLoadingLocations } = useLocations(apiKey);

	const company = user.memberships[0].company;
	const autofill_label = company.autofill_label != '' ? company.autofill_label : 'Customer Auto-fill';
	const reference_label = company.reference_label != '' ? company.reference_label : 'Customer Reference #';

	const currentDate = dayjs().format("MM/DD/YYYY");
	const nextWeekDate = dayjs().add(1, 'week').format('MM/DD/YYYY');

	const defaultFormValues = {
		name 				: '',
		form_id				: 0,
		form_variant_id		: 0,
		reference_no		: '',
		selectedLocation	: company?.div_id ? company?.div_id : "0",
		selectedContact		: user?.cnt_id ? user?.cnt_id : company?.contact_user?.cnt_id ? company?.contact_user?.cnt_id : "0",
		searchQuery			: '',
		customer_id			: 0,
		first_furnishing	: currentDate,
		ff_est				: 0,
		last_furnishing		: nextWeekDate,
		lf_est				: 0,
		total_contract_amt	: '0.00',
		total_claim_amt		: '0.00'
	};

	const [ openClose, setOpenClose ] = useState(false);
	const [ advanced_options, setAdvOpts ] = useState(false);

	const [ forms, setForms ] = useState([]);
	const [ form_variants, setFormVariants ] = useState([]);

	const [ customers, setCustomers ] = useState([]);

	const [ addJobForm, setAddJobForm ] = useState(defaultFormValues);

	// Derive divId for contacts query from form state (single source of truth)
	const effectiveDivId = addJobForm.selectedLocation !== "0" && addJobForm.selectedLocation !== 0 
		? addJobForm.selectedLocation 
		: null;
	
	const { data: contacts = [], isLoading: isLoadingContacts } = useContacts(effectiveDivId, apiKey);

	// const [ workflow_id, setWorkflowID ] = useState(0);
	// const [ selected, setSelected ] = useState([]);
	// const [ title, setTitle ] = useState('');

	useEffect(() => {

		var form_id = 0;
		var form_var_id = 0;

		getAllForms().then(data => {
			var mapped_forms = data.filter(data => data.mapped != 0);

			// if forms are found, set the default-selected form (for adding new jobs) to the first one found
			form_id = mapped_forms.length ? mapped_forms[0].id : null
			form_var_id = addJobForm.form_variant_id
			var variant_list = [];
			var selected_variant = null;

			mapped_forms.map(oData => {
				if ( oData.id == form_id ) {
					variant_list.push( ...oData['form_variant'] )
				}
			})
			if ( variant_list.length ) {
				if ( form_var_id == 0 ) {
					form_var_id = variant_list[0].id
				}
				// Store the selected variant to check for div_id
				selected_variant = variant_list.find(v => v.id == form_var_id) || variant_list[0];
				setFormVariants(variant_list);
			}
			setForms(mapped_forms);
			
			// Determine initial location based on variant's div_id, company default, or first location
			const initialDivId = selected_variant?.div_id || company.div_id || null;
			
			setAddJobForm({
				...addJobForm,
				form_id: form_id,
				form_variant_id: form_var_id,
				// Set initial location if variant has div_id
				...(initialDivId && { selectedLocation: initialDivId })
			})

		}, error => console.error(error))

	}, [])

	// Initialize location and contact defaults when async data loads
	// Location waterfall: variant.div_id (handled in forms useEffect) → company.div_id → first location
	// Contact waterfall: user.cnt_id → company.cnt_id → first contact
	useEffect(() => {
		setAddJobForm(prev => {
			const updates = {};
			
			// Set default location if data is loaded and still at default value
			if (locations.length > 0 && (prev.selectedLocation === "0" || prev.selectedLocation === 0)) {
				updates.selectedLocation = company.div_id || locations[0]?.DIV_ID;
			}

			// Set default contact if data is loaded and still at default value
			if (contacts.length > 0 && (prev.selectedContact === "0" || prev.selectedContact === 0)) {
				updates.selectedContact = user?.cnt_id || company?.contact_user?.cnt_id || contacts[0]?.CNT_ID;
			}
			
			// Only update state if there are changes
			return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
		});
	}, [locations.length, contacts.length, user?.cnt_id, company.div_id, company.cnt_id]);

	// useEffect for opening the advanced options section
	useEffect(() => {
		// if opening the modal's "advanced options" section -> load customers if not already there
		if ( advanced_options ) {
			if ( customers.length === 0 ) {
				listCustomers().then(customers => {
					var customer_list = customers.map(customer => {
						return {
							id: customer.id,
							contact_role: customer.contact_role,
							role_desc: customer.role_desc,
							address: {
								company   : customer.company,
								name      : customer.name,
								phone     : customer.phone,
								address   : customer.address,
								address_2 : customer.address_2,
								city      : customer.city,
								state     : customer.state,
								zip       : customer.zip,
								county    : customer.county,
								email     : customer.email
							}
						}
					});
					setCustomers(customer_list);
				});
			}
		}
	}, [advanced_options]);

	const toggleModal = () => {
		if ( openClose ) {
			setAddJobForm(defaultFormValues);
		}
		setOpenClose( !openClose )
	}

	const handleChange = event => {
		const { target } = event;
		const { type, name, value, checked } = target;

		if ( type == "checkbox" ) {
			if ( checked ) {
				setAddJobForm({ ...addJobForm, [name]: true });
			} else {
				setAddJobForm({ ...addJobForm, [name]: false });
			}
		} else {
			setAddJobForm({ ...addJobForm, [name]: value });
		}
		
	}

	const handleSubmit = (sendLink) => {
		
		props.submit({
			...addJobForm,
			createLink: sendLink ?? false
		});
		
		// TODO: clear these fields and close the form AFTER above submit finishes?
		// reset most of the Add Job form fields
		setAddJobForm({
			...addJobForm, 
			name              : '',
			reference_no      : '',
			searchQuery       : '',
			customer_id       : 0,
			first_furnishing  : currentDate,
			ff_est            : 0,
			last_furnishing   : nextWeekDate,
			lf_est            : 0,
			total_contract_amt: '0.00',
			total_claim_amt   : '0.00'
		})
		setOpenClose(false);

	}

	const update_first_furnishing = value => {
		let new_date = dayjs(value);
		if ( value == '' || !new_date.isValid() ) {
			setAddJobForm({ ...addJobForm, first_furnishing: '' });
		} else {
			setAddJobForm({ ...addJobForm, first_furnishing: formatDate(new_date, 'L') });
		}  
	}

	const update_last_furnishing = value => {
		let new_date = dayjs(value);
		if ( value == '' || !new_date.isValid() ) {
			setAddJobForm({ ...addJobForm, last_furnishing: '' });
		} else {
			setAddJobForm({ ...addJobForm, last_furnishing: formatDate(new_date, 'L') });
		}
	}
  

	const findVariants = target_id => {
		var found_variants = []
		forms.map(form => {
			if ( form.id == target_id && form.form_variant.length > 0 ) {
				found_variants.push(...form.form_variant)
			}
		})
		if ( !found_variants.length ) {
			found_variants.push({
				"id":     0,
				"title": 'Default',
				"file_id": 9,
				"form_id": parseInt(target_id)
			})
		}
		return found_variants;
	}

	// Handle form variant change - update location based on variant's div_id
	const handleVariantChange = (variantId, variantsList = form_variants) => {
		const selectedVariant = variantsList.find(v => v.id == variantId);
		
		// Determine which div_id to use: variant's div_id > company default > first location
		const newDivId = selectedVariant?.div_id || company.div_id || locations[0]?.DIV_ID;
		setAddJobForm(prev => ({
			...prev,
			form_variant_id: parseInt(variantId),
			selectedLocation: newDivId
		}));
	}

	const updateContact = e => {
		const { value } = e.target;
		setAddJobForm({
			...addJobForm,
			selectedContact: value
		});
	}

	const updateLocation = e => {
		const { value } = e.target;
		
		// Find a form variant that matches the selected location's div_id
		const matchingVariant = form_variants.find(v => v.div_id == value);
		
		// Just update form state - contacts query will react automatically via effectiveDivId
		setAddJobForm({
			...addJobForm,
			selectedLocation: value,
			// Update form variant if one matches the selected location
			...(matchingVariant && { form_variant_id: matchingVariant.id }),
		});
	}

	const createSearchList = () => {
		let list = [];
		if( customers ) {
			customers.forEach(cust => {
				let customer_label = cust.address.company + ' (' +cust.address.name+ ')';
				if (cust.role_desc != '') {
					customer_label += ' - ' + cust.role_desc;
				}
				list.push({
					value: cust.id,
					alt: customer_label
				})
			});
		}
		return list;
	}

	const chooseCustomer = e => {
		const { target } = e
		const { value } = target

		if ( !isNaN(value) ) {
			var cust_opt_label = '';
			var chosen_item = parseInt(value);
			// get the customer's label from the customer's array stored in state
			customers.map(cust => {
				if (cust.id == chosen_item) {
					cust_opt_label = cust.address.company + " (" +cust.address.name+ ")";
					if (cust.contact_role != 0) {
						cust_opt_label += " - " +cust.role_desc;
					}
				}
			});
			setAddJobForm({ ...addJobForm, customer_id: chosen_item, searchQuery: cust_opt_label });
		} else {
			setAddJobForm({ ...addJobForm, customer_id: 0, searchQuery: value });
		}
	}

  	return (
		<>
			<Button color="primary" className="rounded-pill px-3" disabled={ props.isSubmitting } onClick={toggleModal}>
				<span className={`fa ${props.isSubmitting ? 'fa-spinner fa-spin' : 'fa-plus'} mr-2 fa-sm`} />
				Create Job
			</Button>
    
			<Modal isOpen={openClose} toggle={toggleModal} style={{ 'maxWidth': '650px' }}>
				<Form onSubmit={handleSubmit}>
					<ModalHeader toggle={toggleModal}>
						<span>Add New Job</span>
					</ModalHeader>
					<ModalBody>
						<div className="pr-3">
							<div className="form-row mb-3">
								<div className="col">
									<Input
										autoFocus={true}
										name="name"
										onChange={handleChange}
										label="New Job Name"
										type="text"
										value={addJobForm.name}
										isRequired={1}
									/>
								</div>

								{forms.length > 1 && (
									<div className="col">
										<ListDropDown
											name="form"
											label="Available Forms"
											selected={addJobForm.form_id}
											onChange={e => {
												let newVariants = findVariants(e.target.value);
												setFormVariants(newVariants);
												
												// Update form_id first, then handle variant change with the new variants
												setAddJobForm(prev => ({
													...prev,
													form_id: parseInt(e.target.value)
												}));
												
												// Handle the variant change to update location based on new variant's div_id
												if (newVariants.length > 0) {
													handleVariantChange(newVariants[0].id, newVariants);
												}
											}}
											limit={1}
											items={forms}
										/>
									</div>
								)}
							</div>

							{form_variants.length > 1 && (
								<div className="form-row mb-3">
									<div className="col">
										<ListDropDown
											name="form_variant"
											label="Form Logo"
											selected={addJobForm.form_variant_id}
											onChange={e => handleVariantChange(e.target.value)}
											limit={0}
											items={form_variants}
										/>
									</div>
								</div>
							)}

							{advanced_options && (
								<>

									<LocationField value={addJobForm.selectedLocation} change={updateLocation} readOnly={!user.api_key || false} />
									<ContactField value={addJobForm.selectedContact} change={updateContact} divId={effectiveDivId} readOnly={!user.api_key || false} />
									
									<div className="form-row mb-3">
										<div className="col-12">
											<div className="Input">
												<div className="Input--label">{reference_label}</div>
												<Input type="text" name="reference_no" value={addJobForm.reference_no} onChange={handleChange} />
											</div>
										</div>
									</div>

									{customers.length > 0 && (
										<div className="form-row mb-3">
											<div className="col-12">
												<div className="Input--label">{autofill_label}</div>
												<div className="input-group" role="group">
													<DataList
														options = {createSearchList()}
														list = "document-types"
														placeHolder = "Search Customers"
														value = { addJobForm.searchQuery }
														onChange = {chooseCustomer}
														disabled={ addJobForm.customer_id > 0 }
													/>
													<span className="input-group-append">
														<button onClick={() => {
															setAddJobForm({
															...addJobForm,
															searchQuery: "",
															customer_id: 0
															})
														}} type="button" className="btn btn-outline-default">
															<i className="fas fa-times"></i>
														</button>
													</span>
												</div>
											</div>
										</div>
									)}

									<div className="form-row mb-3">
										<div className="col-4">
											<div className="Input" style={{ marginRight:"20px" }}>
											<div className="Input--label">First Furnishing Date</div>
												<FormDate
													light
													value={addJobForm.first_furnishing}
													onChange={(date) => update_first_furnishing(date)}
													placeholder="MM/DD/YYYY"
												/>
												<div style={{ marginLeft: '10px' }}>
													<Checkbox
														onChange={handleChange}
														label="Check if estimated"
														name="ff_est"
														value={addJobForm.ff_est == 1 ? true : false}
													/>
												</div>
											</div>
										</div>
										<div className="col-8">
											<div className="Input">
												<div className="Input--label">Last Furnishing Date</div>
												<FormDate
													light
													value={addJobForm.last_furnishing}
													onChange={(date) => update_last_furnishing(date)}
													placeholder="MM/DD/YYYY"
												/>
												<div style={{ marginLeft: '10px' }}>
													<Checkbox
														onChange={handleChange}
														label="Check if estimated"
														name="lf_est"
														value={addJobForm.lf_est == 1 ? true : false}
													/>
												</div>
											</div>
										</div>
									</div>

									<div className="form-row mb-3">
										<div className="col-4">
											<CurrencyField 
												name="total_contract_amt"
												value={addJobForm.total_contract_amt}
												onChange={handleChange}
												label="Total Contract Amount"
											/>
										</div>
										<div className="col-8">
											<CurrencyField 
												name="total_claim_amt"
												value={addJobForm.total_claim_amt}
												onChange={handleChange}
												label="Total Claim Amount (Amount currently owed)"
											/>
										</div>
									</div>
								</>
							)}
							
							<div className="form-row mb-3">
								<div className="col">
									<a href="#" onClick={() => { setAdvOpts(!advanced_options); }} style={{ fontSize: '14px' }}>
										{advanced_options ? (
											<span>Collapse Advanced Options &#9650;</span>
										) : (
											<span>Expand Advanced Options &#9660;</span>
										)}
									</a>
								</div>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button className="mr-2" color="primary" disabled={!addJobForm.name.length}>
							Save
						</Button>
						<Button type="button" className="mr-2" color="secondary" disabled={!addJobForm.name.length}
							onClick={() => handleSubmit(true)}
						>
							Save & Send
						</Button>
						<Button color="secondary" onClick={toggleModal}
						>
							Cancel
						</Button>
					</ModalFooter>
				</Form>
			</Modal>

    	</>
  	)
}
export default AddJobButton;